import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";

import type { Room as LadsRoom } from "../../server/lads-api/clients/lcc-openapi";
import { receiveAllLADSRooms } from "../api/lads";
import { useAccessToken } from "../auth-provider";
import { useRoomsStore } from "../store/rooms-store";
import type { Room } from "../view-model";

async function receiveAllRooms(accessToken?: string): Promise<Room[]> {
  const response = await receiveAllLADSRooms(accessToken);
  return (
    (response.data || [])
      // rooms hidden via lcc-admin should not appear in end-user views
      .filter((room: LadsRoom) => !room.hidden)
      .map((room: LadsRoom) => ({
        id: room.roomId,
        name: room.name || room.roomId,
        contactPerson: room.contact?.name,
        contactData: formatContactData(
          room.contact?.email,
          room.contact?.details
        ),
        roomNumber: room.number,
        airflowMin: room.airflow?.min ?? undefined,
        airflowMax: room.airflow?.max ?? undefined,
        monitoring: (room as any).monitoring?.map(
          (m: { name: string; path: string }) => ({
            name: m.name,
            path: m.path,
          })
        ),
      }))
  );
}

function formatContactData(
  email?: string,
  details?: string
): string | undefined {
  const parts = [email && `email: ${email}`, details].filter(Boolean);
  return parts.length > 0 ? parts.join("\n") : undefined;
}

function getDefaultRoom(rooms?: Room[], defaultRoomId?: string | null) {
  if (rooms) {
    return (
      rooms.find((room) => room.id.toString() === defaultRoomId) || rooms[0]
    );
  }
  return undefined;
}

export function useAllRooms() {
  const { error, isLoading, rooms, setError, setLoading, setRooms } =
    useRoomsStore();
  const accessToken = useAccessToken();

  // Use SWR for initial data fetching and caching
  // Include token in key so SWR refetches when token changes
  const {
    data,
    error: swrError,
    isLoading: swrIsLoading,
    mutate,
  } = useSWR(
    ["allLADSRooms", accessToken || undefined],
    ([, token]) => receiveAllRooms(token),
    {
      refreshInterval: 300_000, // 5 minute polling interval
      revalidateOnFocus: false,
    }
  );

  // Sync SWR loading state to Zustand
  useEffect(() => {
    setLoading(swrIsLoading);
  }, [swrIsLoading, setLoading]);

  // Sync SWR data to Zustand
  useEffect(() => {
    if (data) {
      setRooms(data);
      setLoading(false);
      setError(null);
    }
  }, [data, setRooms, setLoading, setError]);

  // Handle SWR errors
  useEffect(() => {
    if (swrError) {
      setError(swrError.message);
      setLoading(false);
    }
  }, [swrError, setError, setLoading]);

  return {
    rooms,
    error,
    isLoading,
    mutate,
  };
}

export function useActiveRoom() {
  const { rooms } = useAllRooms();
  const [searchParams] = useSearchParams();
  const defaultRoomId = searchParams.get("defaultRoom");
  const { activeRoom, lastUpdated, setActiveRoom } = useRoomsStore();

  // Initialize or refresh active room from fetched rooms data
  useEffect(() => {
    if (rooms.length === 0) {
      // lastUpdated > 0 = the fetch succeeded and the user really has no
      // rooms (e.g. permissions revoked) — drop the stored room instead of
      // keeping a room the server only answers with 403
      if (lastUpdated > 0 && activeRoom) setActiveRoom(undefined);
      return;
    }

    if (activeRoom) {
      const fresh = rooms.find((r) => r.id === activeRoom.id);
      if (!fresh) {
        // stored room is no longer accessible → fall back to an allowed one
        setActiveRoom(getDefaultRoom(rooms, defaultRoomId));
      } else if (JSON.stringify(fresh) !== JSON.stringify(activeRoom)) {
        // Refresh: update activeRoom with latest data (e.g. monitoring)
        setActiveRoom(fresh);
      }
    } else {
      // First load: pick default room
      const defaultRoom = getDefaultRoom(rooms, defaultRoomId);
      if (defaultRoom) setActiveRoom(defaultRoom);
    }
  }, [activeRoom, rooms, lastUpdated, defaultRoomId, setActiveRoom]);

  return {
    activeRoom: activeRoom || getDefaultRoom(rooms, defaultRoomId),
    setActiveRoom,
  };
}
